import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { trackConversion } from "@/lib/tracking";
import { useDeviceCapabilities } from "@/hooks/useDeviceCapabilities";

interface Props {
  to: string;
  children: React.ReactNode;
  variant?: "primary" | "inverted";
  className?: string;
  /** Tracking label for conversion attribution */
  trackLabel?: string;
}

/**
 * GlowCta with built-in conversion tracking.
 * Use on landing pages where CTA clicks need measurement.
 */
const TrackedGlowCta = ({ to, children, variant = "primary", className = "", trackLabel }: Props) => {
  const isPrimary = variant === "primary";
  const { canHover } = useDeviceCapabilities();

  const handleClick = () => {
    if (trackLabel) {
      trackConversion("cta_click", {
        label: trackLabel,
        destination: to,
      });
    }
  };

  return (
    <motion.div
      className="inline-block rounded-full"
      whileHover={
        canHover
          ? {
              scale: 1.025,
              boxShadow: isPrimary
                ? "0 4px 16px hsl(25 20% 18% / 0.1), 0 12px 40px -8px hsl(32 35% 52% / 0.08), 0 24px 64px -16px hsl(25 20% 18% / 0.12)"
                : "0 4px 16px hsl(0 0% 100% / 0.1), 0 12px 40px -8px hsl(0 0% 100% / 0.06), 0 24px 64px -16px hsl(0 0% 0% / 0.1)",
            }
          : undefined
      }
      whileTap={{ scale: 0.97 }}
      transition={{
        duration: 0.5,
        ease: [0.22, 0.68, 0.35, 1],
      }}
    >
      <Link
        to={to}
        onClick={handleClick}
        className={`btn-premium relative overflow-hidden inline-block ${
          isPrimary
            ? "bg-primary text-primary-foreground"
            : "bg-primary-foreground text-primary"
        } px-9 py-[1.05rem] sm:py-3.5 text-[14px] sm:text-[13px] ${className}`}
      >
        <span className="absolute inset-0 -translate-x-full animate-[shimmer_4.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/12 to-transparent" />
        <span className="relative">{children}</span>
      </Link>
    </motion.div>
  );
};

export default TrackedGlowCta;
